// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import * as api from '../../api/client'

/**
 * SaaS-admin: configure a single pharmacy's OWN SMTP mail server. Notifications + invitations for
 * that pharmacy are sent through these credentials (tenant-scoped). Password is write-only.
 */
export default function PharmacyMailServerCard({ pharmacyId }: { pharmacyId: string }) {
  const [t, setT] = useState<api.EmailTransport | null>(null)
  const [host, setHost] = useState('')
  const [port, setPort] = useState('587')
  const [security, setSecurity] = useState('starttls')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [fromEmail, setFromEmail] = useState('')
  const [senderName, setSenderName] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [testTo, setTestTo] = useState('')
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string | null } | null>(null)
  const [testing, setTesting] = useState(false)
  const [outbox, setOutbox] = useState<api.EmailOutbox | null>(null)
  const [draining, setDraining] = useState(false)

  async function loadOutbox() { try { setOutbox(await api.getPharmacyEmailOutbox(pharmacyId)) } catch {} }
  async function drainNow() {
    setDraining(true)
    try { await api.drainPharmacyEmailOutbox(pharmacyId); await loadOutbox() }
    catch {}
    finally { setDraining(false) }
  }
  async function retryFailed() {
    setDraining(true)
    try { await api.retryPharmacyEmailOutbox(pharmacyId); await loadOutbox() }
    catch {}
    finally { setDraining(false) }
  }

  function hydrate(d: api.EmailTransport) {
    setT(d)
    setHost(d.smtp_host || ''); setPort(String(d.smtp_port ?? 587)); setSecurity(d.smtp_security || 'starttls')
    setUsername(d.smtp_username || ''); setFromEmail(d.from_email || ''); setSenderName(d.sender_name || '')
    setEnabled(!!d.smtp_enabled); setPassword('')
  }
  useEffect(() => { api.getPharmacyEmailTransport(pharmacyId).then(hydrate).catch(() => {}); loadOutbox() }, [pharmacyId])

  async function save() {
    setBusy(true); setMsg(null)
    try {
      await api.putPharmacyEmailTransport(pharmacyId, {
        smtp_host: host || null, smtp_port: port ? Number(port) : null, smtp_security: security,
        smtp_username: username || null, smtp_password: password || null, smtp_enabled: enabled,
        from_email: fromEmail || null, sender_name: senderName || null,
      })
      setPassword('')
      const d = await api.getPharmacyEmailTransport(pharmacyId); hydrate(d)
      setMsg('Saved.')
    } catch (e: any) { setMsg(e?.message || 'Save failed.') }
    finally { setBusy(false) }
  }

  async function sendTest() {
    setTesting(true); setTestResult(null)
    try { setTestResult(await api.testPharmacyEmailTransport(pharmacyId, testTo.trim())) }
    catch (e: any) { setTestResult({ ok: false, error: e?.message || 'Test failed.' }) }
    finally { setTesting(false) }
  }

  const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginTop: 16, background: 'var(--color-surface)' }
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--color-text-mute)', display: 'block', marginBottom: 3 }
  const input: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 7, border: '1px solid var(--color-border)', fontSize: 13, fontFamily: 'inherit' }
  const row: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <h3 style={{ fontSize: 15, margin: 0 }}>Mail server (SMTP)</h3>
        <label style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} /> Enabled
        </label>
      </div>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 12 }}>
        This pharmacy's notifications and invitations are sent through this server, from their own domain. Credentials are encrypted and scoped to this tenant only.
      </div>

      <div style={row}>
        <div><label style={label}>SMTP host</label><input style={input} value={host} onChange={e => setHost(e.target.value)} placeholder="smtp.theirdomain.com" /></div>
        <div><label style={label}>Port</label><input style={input} value={port} onChange={e => setPort(e.target.value)} placeholder="587" inputMode="numeric" /></div>
      </div>
      <div style={row}>
        <div><label style={label}>Security</label>
          <select style={input} value={security} onChange={e => setSecurity(e.target.value)}>
            <option value="starttls">STARTTLS (587)</option>
            <option value="ssl">SSL/TLS (465)</option>
            <option value="none">None (25)</option>
          </select>
        </div>
        <div><label style={label}>Username</label><input style={input} value={username} onChange={e => setUsername(e.target.value)} placeholder="apikey / mailbox" autoComplete="off" /></div>
      </div>
      <div style={row}>
        <div><label style={label}>Password {t?.has_password ? '(leave blank to keep)' : ''}</label>
          <input style={input} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={t?.has_password ? '•••••••• unchanged' : ''} autoComplete="new-password" />
        </div>
        <div><label style={label}>From address</label><input style={input} value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="notifications@theirdomain.com" /></div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={label}>Sender name (optional)</label><input style={input} value={senderName} onChange={e => setSenderName(e.target.value)} placeholder="Their Pharmacy" />
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button onClick={save} disabled={busy} style={{ background: 'var(--color-primary,#2563eb)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {msg && <span style={{ fontSize: 12.5, color: msg === 'Saved.' ? 'var(--color-success,#15803d)' : '#B23A22' }}>{msg}</span>}
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 14, paddingTop: 12 }}>
        <label style={label}>Send a test email</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input style={{ ...input, maxWidth: 280 }} value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@example.com" />
          <button onClick={sendTest} disabled={testing} style={{ background: 'none', border: '1px solid var(--color-primary,#2563eb)', color: 'var(--color-primary,#2563eb)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: testing ? 'default' : 'pointer', fontFamily: 'inherit' }}>
            {testing ? 'Sending…' : 'Send test'}
          </button>
        </div>
        {testResult && (
          <div style={{ fontSize: 12.5, marginTop: 8, color: testResult.ok ? 'var(--color-success,#15803d)' : '#B23A22' }}>
            {testResult.ok ? '✓ Test email sent successfully.' : `✗ ${testResult.error || 'Failed.'}`}
          </div>
        )}
        {t?.last_test_at && !testResult && (
          <div style={{ fontSize: 11.5, marginTop: 8, color: 'var(--color-text-mute)' }}>
            Last test {new Date(t.last_test_at).toLocaleString()} — {t.last_test_ok ? 'OK' : `failed: ${t.last_test_error || ''}`}
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 14, paddingTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <label style={label}>Delivery</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={loadOutbox} style={{ background: 'none', border: 'none', color: 'var(--color-text-mute)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>Refresh</button>
            <button onClick={drainNow} disabled={draining} style={{ background: 'none', border: '1px solid var(--color-border)', borderRadius: 7, padding: '4px 10px', fontSize: 12, cursor: draining ? 'default' : 'pointer', fontFamily: 'inherit' }}>{draining ? 'Sending…' : 'Send queued now'}</button>
            {outbox && (outbox.counts['failed'] ?? 0) > 0 && (
              <button onClick={retryFailed} disabled={draining} style={{ background: 'none', border: '1px solid #B23A22', color: '#B23A22', borderRadius: 7, padding: '4px 10px', fontSize: 12, cursor: draining ? 'default' : 'pointer', fontFamily: 'inherit' }}>{draining ? 'Retrying…' : `Retry failed (${outbox.counts['failed']})`}</button>
            )}
          </div>
        </div>
        {outbox && (
          <>
            <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 8 }}>
              {Object.keys(outbox.counts).length === 0 ? 'No mail queued.' : Object.entries(outbox.counts).map(([s, n]) => `${n} ${s}`).join(' · ')}
            </div>
            {outbox.recent.length > 0 && (
              <div style={{ fontSize: 12 }}>
                {outbox.recent.slice(0, 8).map((m, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0', borderTop: i ? '1px solid var(--color-border)' : 'none' }}>
                    <span style={{ color: 'var(--color-text-mute)', whiteSpace: 'nowrap' }}>{m.kind}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{m.recipient}</span>
                    <span style={{ fontWeight: 600, color: m.status === 'sent' ? 'var(--color-success,#15803d)' : m.status === 'failed' ? '#B23A22' : 'var(--color-text-mute)' }} title={m.last_error || ''}>{m.status}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
